import React, { useMemo } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Card, CardContent } from "@/components/ui/card";
import { AlertTriangle, Info, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

// Distinct, accessible palette for up to 5 series. Order matters: index 0..4.
const PALETTE = [
  "#14b8a6", // teal-500
  "#f59e0b", // amber-500
  "#8b5cf6", // violet-500
  "#ef4444", // red-500
  "#3b82f6", // blue-500
];

const fmtNumber = (n) =>
  typeof n === "number" ? n.toLocaleString("es-MX") : "—";

/**
 * Phase 5.4 — N-series time chart for the comparatives module.
 *
 * `result` is the payload returned by POST /api/comparatives/run or
 * /api/comparatives/:id/run. Shape:
 *   {
 *     types: ["RF PICK", "RF STAGE", ...],
 *     labels: ["2026-01", ...],
 *     series: [
 *       { type, values: [...], counts: [...], total, tx_per_hour_total }
 *     ],
 *     metric: "count" | "tx_per_hour",
 *     ...
 *   }
 */
export default function ComparativeChart({ result, loading = false, error = null }) {
  const { t } = useTranslation();
  const isRate = result?.metric === "tx_per_hour";
  const coverage = result?.hours_coverage || null;
  const missingPeriods = coverage?.periods_missing_hours || [];
  const hasAnyHours = coverage?.has_any_hours;

  const chartData = useMemo(() => {
    if (!result || !result.labels) return [];
    return result.labels.map((lab, i) => {
      const row = { label: lab };
      result.series.forEach((s) => {
        row[s.type] = s.values?.[i] ?? null;
        row[`__count_${s.type}`] = s.counts?.[i] ?? null;
      });
      return row;
    });
  }, [result]);

  if (loading) {
    return (
      <Card data-testid="comparative-chart-loading">
        <CardContent className="flex h-72 items-center justify-center text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Cargando…
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card
        data-testid="comparative-chart-error"
        className="border-destructive/40"
      >
        <CardContent className="flex h-64 flex-col items-center justify-center gap-2 p-6 text-center">
          <div className="text-sm font-medium text-destructive">
            No se pudo ejecutar este comparativo
          </div>
          <div className="max-w-md text-xs text-muted-foreground">
            {error}
          </div>
          <div className="text-[11px] text-muted-foreground">
            Revisa el detalle en la pestaña Network o intenta con otro
            preset. Si el problema persiste, reporta este mensaje.
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!result) {
    return (
      <Card>
        <CardContent className="flex h-64 items-center justify-center text-sm text-muted-foreground">
          Selecciona un comparativo para visualizarlo.
        </CardContent>
      </Card>
    );
  }

  if (chartData.length === 0) {
    return (
      <Card>
        <CardContent
          className="flex h-64 items-center justify-center text-sm text-muted-foreground"
          data-testid="comparative-chart-empty"
        >
          Sin datos para los filtros aplicados.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="comparative-chart">
      <CardContent className="space-y-3 pt-4">
        {/* Phase 9.6 — hours-coverage banner: explains why tx/hr comes back
            empty when the filtered range has no time_entries. */}
        {isRate && coverage && !hasAnyHours && (
          <div
            data-testid="comparative-no-hours-banner"
            className="flex items-start gap-2 rounded-md border border-amber-500/50 bg-amber-500/10 p-3 text-xs"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
            <div className="space-y-1">
              <div className="font-medium text-amber-500">
                {t("comparativo.noHoursTitle")}
              </div>
              <div className="text-muted-foreground">
                {t("comparativo.noHoursHelp")}
              </div>
              {missingPeriods.length > 0 && (
                <div className="text-[11px] text-muted-foreground">
                  {t("comparativo.missingPeriodsHours", {
                    periods: missingPeriods.join(", "),
                  })}
                </div>
              )}
            </div>
          </div>
        )}
        {isRate && coverage && hasAnyHours && missingPeriods.length > 0 && (
          <div
            data-testid="comparative-partial-hours-banner"
            className="flex items-start gap-2 rounded-md border border-border bg-muted/40 p-2 text-[11px] text-muted-foreground"
          >
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              {t("comparativo.missingPeriodsHours", {
                periods: missingPeriods.join(", "),
              })}
            </span>
          </div>
        )}

        {/* Totals strip */}
        <div className="flex flex-wrap gap-2">
          {result.series.map((s, idx) => {
            const color = PALETTE[idx % PALETTE.length];
            return (
              <div
                key={s.type}
                data-testid={`kpi-total-${s.type}`}
                className="rounded-md border border-border bg-muted/30 px-2 py-1"
              >
                <div
                  className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider"
                  style={{ color }}
                >
                  <span
                    className="inline-block h-1.5 w-1.5 rounded-full"
                    style={{ background: color }}
                  />
                  {s.type}
                </div>
                <div className="text-base font-bold tabular-nums">
                  {isRate
                    ? s.tx_per_hour_total != null
                      ? `${s.tx_per_hour_total.toFixed(2)} tx/hr`
                      : "—"
                    : fmtNumber(s.total)}
                </div>
                {isRate && (
                  <div className="text-[10px] text-muted-foreground">
                    {fmtNumber(s.total)} total
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Chart */}
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11 }}
              stroke="#71717a"
            />
            <YAxis
              tick={{ fontSize: 11 }}
              stroke="#71717a"
              label={
                isRate
                  ? {
                      value: "tx/hr",
                      angle: -90,
                      position: "insideLeft",
                      style: { fill: "#a1a1aa", fontSize: 11 },
                    }
                  : undefined
              }
            />
            <Tooltip
              contentStyle={{
                background: "hsl(var(--card))",
                border: "1px solid hsl(var(--border))",
                borderRadius: 6,
                fontSize: 12,
              }}
              formatter={(value, key, item) => {
                if (!isRate) return fmtNumber(value);
                const raw = item?.payload?.[`__count_${key}`];
                return [
                  `${typeof value === "number" ? value.toFixed(2) : "—"} tx/hr`,
                  `${key} (count: ${fmtNumber(raw)})`,
                ];
              }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {result.series.map((s, idx) => (
              <Line
                key={s.type}
                type="monotone"
                dataKey={s.type}
                stroke={PALETTE[idx % PALETTE.length]}
                strokeWidth={2.5}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>

        {/* Breakdown by employee table — only when result has it. */}
        {result.by_employee && result.by_employee.length > 0 && (
          <div data-testid="by-employee-table" className="overflow-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="px-2 py-1">Empleado</th>
                  {result.types.map((t, i) => (
                    <th
                      key={t}
                      className="px-2 py-1 text-right tabular-nums"
                      style={{ color: PALETTE[i % PALETTE.length] }}
                    >
                      {t}
                    </th>
                  ))}
                  <th className="px-2 py-1 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {result.by_employee.slice(0, 30).map((row) => (
                  <tr
                    key={row.employee_id || row.employee_name}
                    className="border-b border-border/40"
                    data-testid={`emp-row-${row.employee_id || ""}`}
                  >
                    <td className="px-2 py-1">
                      <span className="text-muted-foreground">
                        {row.employee_id || "—"}
                      </span>{" "}
                      {row.employee_name}
                    </td>
                    {result.types.map((t, i) => (
                      <td
                        key={t}
                        className="px-2 py-1 text-right tabular-nums"
                        style={{ color: PALETTE[i % PALETTE.length] }}
                      >
                        {fmtNumber(row.counts?.[t] ?? 0)}
                      </td>
                    ))}
                    <td className="px-2 py-1 text-right font-medium tabular-nums">
                      {fmtNumber(row.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Breakdown by department table (Phase 9.6). */}
        {result.by_department && result.by_department.length > 0 && (
          <div data-testid="by-department-table" className="overflow-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="px-2 py-1">{t("common.department")}</th>
                  {result.types.map((tt, i) => (
                    <th
                      key={tt}
                      className="px-2 py-1 text-right tabular-nums"
                      style={{ color: PALETTE[i % PALETTE.length] }}
                    >
                      {tt}
                    </th>
                  ))}
                  {isRate && (
                    <th className="px-2 py-1 text-right">
                      {t("typeComparison.hours")}
                    </th>
                  )}
                  <th className="px-2 py-1 text-right">{t("common.total")}</th>
                </tr>
              </thead>
              <tbody>
                {result.by_department.map((row) => (
                  <tr
                    key={row.department}
                    className="border-b border-border/40"
                    data-testid={`dept-row-${row.department}`}
                  >
                    <td className="px-2 py-1">{row.department}</td>
                    {result.types.map((tt, i) => (
                      <td
                        key={tt}
                        className="px-2 py-1 text-right tabular-nums"
                        style={{ color: PALETTE[i % PALETTE.length] }}
                      >
                        {isRate
                          ? row.tx_per_hour?.[tt] != null
                            ? `${row.tx_per_hour[tt].toFixed(2)}`
                            : "—"
                          : fmtNumber(row.counts?.[tt] ?? 0)}
                      </td>
                    ))}
                    {isRate && (
                      <td className="px-2 py-1 text-right tabular-nums text-muted-foreground">
                        {row.hours_worked != null
                          ? row.hours_worked.toLocaleString("es-MX", {
                              maximumFractionDigits: 1,
                            })
                          : t("typeComparison.noHours")}
                      </td>
                    )}
                    <td className="px-2 py-1 text-right font-medium tabular-nums">
                      {fmtNumber(row.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
